/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";

// Helper Imports
import { formatPaymentProperties } from "../../../../Helpers/formatters";
import { validatePaymentDetails } from "../../../../Helpers/validateInputData";

// MUI Imports
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import EditIcon from "@mui/icons-material/Edit";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";

// Component Imports
import EditModal from "../../../../Components/EditModal";
import Loading from "../../../../Components/Interactive/Loading";

const CustomerPaymentDetails = ({
  allCustomerData,
  loading,
  setCustomerData,
  setUpdatedData,
  setTrigger,
}) => {
  // Data
  const {
    userPaymentInfo: {
      cardType,
      expirationDate,
      lastFour,
      cardHolder,
      bank,
      type,
      isPrepaid,
    },
    userDetailsInfo: { address1, city, state, postalCode, customerId },
    userOrders: { orders },
  } = allCustomerData;

  const userAuth = useSelector((state) => state.auth);
  const userId = userAuth?.userInfo?.userData?.userId;
  const role = userAuth?.userInfo?.userData?.role;

  // Inner State
  const [open, setIsOpen] = useState(true);
  const [toggleDropdown, setToggleDropdown] = useState(true);
  const [modalIsOpen, setModalOpen] = useState(false);
  const [customerBillingAddress, setBillingAddress] = useState("");
  const [customerBillingCity, setBillingCity] = useState("");
  const [customerBillingState, setBillingState] = useState("");
  const [customerBillingZip, setBillingZip] = useState("");

  const [initialState, setInitialState] = useState({
    address1,
    city,
    state,
    postalCode,
  });

  // Get customers current data
  useEffect(() => {
    setBillingAddress(address1);
    setBillingCity(city);
    setBillingState(state);
    setBillingZip(postalCode);
    setInitialState({ address1, city, state, postalCode });
  }, [address1, city, state, postalCode]);

  // Event Handlers
  const handleOpen = (e) => setModalOpen(true);
  const handleClose = (e) => {
    setBillingAddress(initialState.address1);
    setBillingCity(initialState.city);
    setBillingState(initialState.state);
    setBillingZip(initialState.postalCode);
    setModalOpen(false);
  };

  // Send request to update the users credit card information to their email
  const submitUpdateCCEmail = async () => {
    if (orders) {
      const { orderId } = orders[orders.length - 1];
      const options = {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          OrderId: orderId,
          UserId: userId,
        }),
      };
      const res = await fetch(
        `/api/customer/${customerId}/update-credit`,
        options
      );

      const { success } = await res.json();

      setUpdatedData((prevState) => ({
        ...prevState.updateDataSuccess,
        updateData: {
          ...prevState.updateData,
          state: success ? true : false,
          message: success
            ? "Request to update users credit card information sent successfully"
            : "Failed to send credit card update email",
        },
      }));
      setTrigger("UPDATE_EMAILS");
      setTrigger("");
    }
  };

  const submitPaymentUpdate = async () => {
    const error = validatePaymentDetails(
      customerBillingCity,
      customerBillingState,
      customerBillingAddress,
      customerBillingZip
    );
    if (error) return;

    // Send API request to update user data and update the top level allCustomerData state
    const options = {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        city: customerBillingCity,
        state: customerBillingState,
        address1: customerBillingAddress,
        postalCode: customerBillingZip,
      }),
    };
    const res = await fetch(
      `/api/customer/${customerId}/update-payment`,
      options
    );
    const { success } = await res.json();

    setCustomerData((prevState) => ({
      userDetailsInfo: {
        ...prevState.userDetailsInfo,
        address1: customerBillingAddress,
        city: customerBillingCity,
        state: customerBillingState,
        postalCode: customerBillingZip,
      },
      userOrders: prevState.userOrders,
      userPaymentInfo: {
        ...prevState.userPaymentInfo,
        userPaymentInfo: { cardType, expirationDate, lastFour, cardHolder },
      },
      userCallNotesInfo: prevState.userCallNotesInfo,
      userEmailNotesInfo: prevState.userEmailNotesInfo,
      userMembershipInfo: prevState.userMembershipInfo,
      userTrackingInfo: prevState.userTrackingInfo,
      userProgramsInfo: prevState.userProgramsInfo,
      userLoginHistoryInfo: prevState.userLoginHistoryInfo,
      dispositions: prevState.dispositions,
      userEvents: prevState.userEvents,
      userReportHistory: prevState.userReportHistory,
      userCallLog: prevState.userCallLog,
      userLockouts: prevState.userLockouts,
      userReportDownloads: prevState.userReportDownloads
    }));
    setUpdatedData((prevState) => ({
      updateData: {
        ...prevState.updateData,
        state: success ? true : false,
        message: success ? "User payment details updated" : "",
      },
    }));
    setModalOpen(false);
  };

  const isDefined = () =>
    cardType ||
    expirationDate ||
    lastFour ||
    cardHolder ||
    address1 ||
    city ||
    state ||
    postalCode;

  return (
    <div
      className={`user-data-container ${
        open ? "show-payment-widget" : "hide-payment-widget"
      }${cardType ? "" : "user-data-container-squeezed"}`}
      style={{marginBottom: "1rem"}}
    >
      <div className="customer-widget-header">
        <p className="customer-widget-heading">Payment</p>
        {open && cardType && (
          <EditIcon className="edit-info-btn" onClick={handleOpen} />
        )}
        {cardType && (
          <ArrowDropDownIcon
            onClick={() => {
              setIsOpen(!open);
              setToggleDropdown(!toggleDropdown);
            }}
            className={`arrow-dropdown ${
              toggleDropdown ? "arrow-open" : "arrow-close"
            }`}
          />
        )}
      </div>
      <div className={`data-push ${open ? "show-data" : "no-data"}`}>
        {loading ? (
          <div className="loading-widget">
            <Loading />
          </div>
        ) : (
          <>
            {isDefined() ? (
              formatPaymentProperties(
                cardType,
                expirationDate,
                lastFour,
                cardHolder,
                address1,
                city,
                state,
                postalCode,
                type,
                isPrepaid,
                bank,
                role
              )
            ) : (
              <h3>No Payment Details</h3>
            )}
            {cardType && (
              <button className="update-ccEmail-btn">
                <span onClick={submitUpdateCCEmail}>
                  Update Credit Card email
                </span>
              </button>
            )}
            <EditModal open={modalIsOpen} handleClose={handleClose}>
              <div className="modal-header">
                <h2 style={{ padding: ".5rem", color: "#6acde6" }}>
                  Edit Payment Details
                </h2>
                <CloseIcon
                  className="close-icons"
                  onClick={() => handleClose()}
                />
              </div>
              <TextField
                id="outlined-full-width"
                className="input-el"
                label="Street Address"
                name="street_address"
                style={{ margin: 10, width: "96%" }}
                fullWidth={false}
                margin="normal"
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                value={customerBillingAddress}
                onChange={(e) => setBillingAddress(e.target.value)}
              />
              <TextField
                id="outlined-full-width"
                className="input-el"
                label="City"
                name="city_name"
                style={{ margin: 8, width: "96%" }}
                fullWidth={false}
                margin="normal"
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                value={customerBillingCity}
                onChange={(e) => setBillingCity(e.target.value)}
              />
              <TextField
                id="outlined-full-width"
                className="input-el"
                label="State"
                name="state"
                style={{ margin: 8, width: "96%" }}
                fullWidth={false}
                margin="normal"
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                value={customerBillingState}
                onChange={(e) => setBillingState(e.target.value)}
              />
              <TextField
                id="outlined-full-width"
                className="input-el"
                label="Postal Code"
                name="postal"
                style={{ margin: 8, width: "96%" }}
                fullWidth={false}
                margin="normal"
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                value={customerBillingZip}
                onChange={(e) => setBillingZip(e.target.value)}
              />
              <div className="modal-btn-box">
                <button
                  className="submit-btn-payment"
                  onClick={submitPaymentUpdate}
                >
                  <span>Submit</span>
                </button>
              </div>
            </EditModal>
          </>
        )}
      </div>
    </div>
  );
};

export default CustomerPaymentDetails;
